import type { LogicalAlignment } from '@wix/editor-elements-common-utils'
import { withSiteDirection } from '@wix/editor-elements-common-utils'
import { withCompInfo } from '@wix/editor-elements-integrations'
import type { ExpandableMenuCSSVars, ExpandableMenuDefinition, MenuItemProps } from '@wix/thunderbolt-components-native'
import { filterItemsWithSlot } from '@wix/thunderbolt-commons'
import type { ExpandableMenuProps } from './ExpandableMenu'

type GridSettings = {
	templateColumns: string
	templateAreas: string
}

const getGridSettings = (itemsAlignment: LogicalAlignment, isReversed: boolean): GridSettings =>
	itemsAlignment === 'center'
		? {
			templateColumns: 'calc(40px + 1em) 1fr calc(40px + 1em)',
			templateAreas: isReversed ? '"arrow label ."' : '". label arrow"',
		}
		: {
			templateColumns: isReversed ? 'calc(40px + 1em) 1fr' : '1fr calc(40px + 1em)',
			templateAreas: isReversed ? '"arrow label"' : '"label arrow"',
		}

export const css = withCompInfo<ExpandableMenuCSSVars, ExpandableMenuDefinition>()(
	['compData', 'compProps', 'styleProperties', 'experiments'],
	({ compData, compProps, styleProperties }) => {
		const {
			direction = 'inherit',
			itemDirection = 'inherit',
			submenuDirection = 'inherit',
			submenuSubItemDirection = 'inherit',
		} = compData
		const {
			menuSpacing,
			subMenuSpacing,
			layoutDirection,
			align,
			itemAlign = 'inherit',
			submenuAlign = 'inherit',
			submenuSubItemAlign = 'inherit',
		} = styleProperties
		const menuItemHeight = compProps.menuItemHeight
		const itemHeight = `${menuItemHeight === null ? 50 : menuItemHeight}px`
		const gridSettings = getGridSettings(align as LogicalAlignment, layoutDirection === 'row-reverse')

		const paddingStart = align === 'start' ? menuSpacing : '0px'
		const subPaddingStart = align === 'start' ? subMenuSpacing : '0px'
		const paddingEnd = align === 'end' ? menuSpacing : '0px'
		const subPaddingEnd = align === 'end' ? subMenuSpacing : '0px'

		return {
			height: 'auto',
			'--direction': withSiteDirection(direction),
			'--item-height': itemHeight,
			'--text-align': align as LogicalAlignment,
			'--template-columns': gridSettings.templateColumns,
			'--template-areas': gridSettings.templateAreas,
			'--padding-start': paddingStart,
			'--sub-padding-start': subPaddingStart,
			'--padding-end': paddingEnd,
			'--sub-padding-end': subPaddingEnd,
			'--item-depth0-direction': itemDirection,
			'--item-depth1-direction': submenuDirection,
			'--item-depth2-direction': submenuSubItemDirection,
			'--item-depth0-align': itemAlign,
			'--item-depth1-align': submenuAlign,
			'--item-depth2-align': submenuSubItemAlign,
		}
	}
)

export const props = withCompInfo<ExpandableMenuProps, ExpandableMenuDefinition>()(
	['compData', 'skin'],
	({ compData, skin }) => {
		const { menuRef } = compData
		return {
			skin,
			items: filterItemsWithSlot((menuRef as unknown) as Array<MenuItemProps>, true),
			currentPrimaryPageHref: '',
		}
	}
)
